/**
 * My Calendar — the recruiter's personal calendar.
 *
 * One view over three sources (GET /api/crm/calendar): booked callbacks (cyan),
 * follow-up tasks with a due date (amber), and the recruiter's own personal
 * events (user-chosen color). Callbacks/tasks are read-only here — clicking one
 * jumps to its contact; only personal events are created/edited inline.
 *
 * "Calendars" you can show are of two kinds, both filterable:
 *   • Event types — toggle Callbacks / Tasks / Personal events on/off (everyone).
 *   • Recruiters — admins can overlay several teammates' callbacks & tasks at
 *     once (or "All recruiters"). When more than one person is shown, items are
 *     color-coded per person (chip fill still encodes the event type). Personal
 *     events always stay private to their owner, so they appear only when your
 *     own calendar is part of the selection.
 *
 * Month grid + a rolling 30-day agenda. Times render in the viewer's local zone.
 * Refetches on a 60s poll and on the cross-surface crm:stats-changed event, the
 * same rhythm as the sidebar badges, so a callback booked elsewhere shows up.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  X,
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { CalendarItem, CalendarSourceKind } from '../types';
import { clockTime, dayKey, dayLabel, getPref, setPref, STATS_CHANGED_EVENT, useIsCRMAdmin, useTeam } from '../lib';
import { RefreshButton, SegmentedControl } from '../components';
import EventModal from '../components/calendar/EventModal';
import RecruiterMultiSelect, { AssigneeSelection } from '../components/calendar/RecruiterMultiSelect';
import { DEFAULT_EVENT_COLOR, eventColor, personColor } from '../components/calendar/colors';
import { expandOccurrences } from '../components/calendar/recurrence';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const pad = (n: number) => String(n).padStart(2, '0');
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfWeekSun = (d: Date) => addDays(startOfDay(d), -startOfDay(d).getDay());
const localDayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const firstName = (n: string | null | undefined) => (n || '').trim().split(/\s+/)[0] || n || '';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const chipBase = 'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium';

// Unique per rendered instance — recurring occurrences share a source_id, so the
// per-occurrence instance_key keeps React keys distinct.
const keyOf = (it: CalendarItem) => `${it.source_kind}-${it.source_id}-${it.instance_key ?? ''}`;

// The three "event-type calendars" the legend toggles.
const ALL_KINDS: CalendarSourceKind[] = ['callback', 'task', 'event'];
const KIND_META: Record<CalendarSourceKind, { label: string; dot: string }> = {
  callback: { label: 'Callbacks', dot: 'bg-cyan-500' },
  task: { label: 'Tasks', dot: 'bg-amber-500' },
  event: { label: 'Personal events', dot: 'bg-slate-500' },
};

function loadKinds(): Set<CalendarSourceKind> {
  const parts = getPref('calendarKinds', ALL_KINDS.join(','))
    .split(',')
    .map((s) => s.trim())
    .filter((k): k is CalendarSourceKind => (ALL_KINDS as string[]).includes(k));
  return new Set(parts.length ? parts : ALL_KINDS);
}

function loadAssignees(meId: string | undefined): AssigneeSelection {
  const raw = getPref('calendarAssignees', '');
  if (raw === 'all') return { all: true, ids: [] };
  const ids = raw.split(',').map((s) => s.trim()).filter(Boolean);
  if (ids.length) return { all: false, ids };
  return { all: false, ids: meId ? [meId] : [] };
}

function isTerminal(item: CalendarItem): boolean {
  if (item.source_kind === 'callback') return ['completed', 'cancelled', 'no_show'].includes(item.status || '');
  if (item.source_kind === 'task') return ['done', 'cancelled'].includes(item.status || '');
  return false;
}

function chipCls(item: CalendarItem): string {
  if (item.source_kind === 'callback') return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300';
  if (item.source_kind === 'task') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  return eventColor(item.color).chip;
}

function dotCls(item: CalendarItem): string {
  if (item.source_kind === 'callback') return 'bg-cyan-500';
  if (item.source_kind === 'task') return 'bg-amber-500';
  return eventColor(item.color).dot;
}

type ModalState =
  | { mode: 'create'; start: Date; end: Date; allDay: boolean; color: string }
  | {
      mode: 'edit';
      eventId: string;
      title: string;
      start: Date;
      end: Date;
      allDay: boolean;
      location: string | null;
      description: string | null;
      color: string | null;
      recurrence: 'weekly' | null;
      recurrenceDays: number[] | null;
      recurrenceUntil: string | null;
      blocksBooking: boolean;
    };

export default function Calendar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = useIsCRMAdmin();
  const { team, byId } = useTeam();

  const [view, setView] = useState<'month' | 'agenda'>(() =>
    getPref('calendarView', 'month') === 'agenda' ? 'agenda' : 'month'
  );
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [assignees, setAssignees] = useState<AssigneeSelection>(() => loadAssignees(user?.id));
  const [visibleKinds, setVisibleKinds] = useState<Set<CalendarSourceKind>>(() => loadKinds());
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [dayDetail, setDayDetail] = useState<Date | null>(null);

  // Non-admins only ever see their own calendar (the picker is hidden for them,
  // but guard the effective selection too).
  const effAssignees: AssigneeSelection = isAdmin
    ? assignees
    : { all: false, ids: user?.id ? [user.id] : [] };

  // More than one recruiter on screen → color-code by person.
  const multiPerson = isAdmin && (effAssignees.all || effAssignees.ids.length > 1);
  // Showing anyone other than just yourself (their personal events stay private).
  const includesOthers = isAdmin && (effAssignees.all || effAssignees.ids.some((id) => id !== user?.id));
  // You can add events whenever your own calendar is part of the view (so the
  // new event actually shows up).
  const canCreate = effAssignees.all || (!!user?.id && effAssignees.ids.includes(user.id));

  // Wire the selection to the query: omit the param for the plain "just me" case
  // so non-admins / default views take the unauthenticated-to-others fast path.
  const assigneeParam = useMemo(() => {
    if (!isAdmin) return null;
    if (assignees.all) return 'all';
    const ids = assignees.ids;
    if (ids.length === 0) return null;
    if (ids.length === 1 && ids[0] === user?.id) return null;
    return ids.join(',');
  }, [isAdmin, assignees, user?.id]);

  // If auth resolved after first render (no stored pref, id was unknown), seed
  // the selection to "just me" so the default view and event creation work.
  useEffect(() => {
    if (user?.id && !assignees.all && assignees.ids.length === 0) {
      setAssignees(loadAssignees(user.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const setViewPref = (v: 'month' | 'agenda') => {
    setView(v);
    setPref('calendarView', v);
  };

  const updateAssignees = (v: AssigneeSelection) => {
    setAssignees(v);
    setPref('calendarAssignees', v.all ? 'all' : v.ids.join(','));
  };

  const toggleKind = (k: CalendarSourceKind) => {
    const next = new Set(visibleKinds);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setVisibleKinds(next);
    setPref('calendarKinds', [...next].join(','));
  };

  const range = useMemo(() => {
    if (view === 'agenda') {
      const from = startOfDay(new Date());
      return { from, to: addDays(from, 30) };
    }
    const gridStart = startOfWeekSun(startOfMonth(anchor));
    return { from: gridStart, to: addDays(gridStart, 42) };
  }, [view, anchor]);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const params: Record<string, string> = { from: range.from.toISOString(), to: range.to.toISOString() };
        if (assigneeParam) params.assignees = assigneeParam;
        const res = await api.get('/api/crm/calendar', { params });
        setItems((res.data?.events || []) as CalendarItem[]);
        setError(null);
      } catch (e: any) {
        setError(errMsg(e));
      } finally {
        setLoading(false);
      }
    },
    [range, assigneeParam]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const silent = () => load(true);
    const timer = window.setInterval(silent, 60_000);
    window.addEventListener(STATS_CHANGED_EVENT, silent);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(STATS_CHANGED_EVENT, silent);
    };
  }, [load]);

  // Event-type filter (the per-type "calendars") applied before grouping.
  const filteredItems = useMemo(
    () => items.filter((it) => visibleKinds.has(it.source_kind)),
    [items, visibleKinds]
  );

  // The recurring base rows keep the true series start/end + rule, so editing
  // any occurrence edits the whole series. Looked up by source_id on click.
  const baseEventsById = useMemo(() => {
    const m = new Map<string, CalendarItem>();
    for (const it of filteredItems) if (it.source_kind === 'event') m.set(it.source_id, it);
    return m;
  }, [filteredItems]);

  // Expand recurring events into per-occurrence items within the visible window.
  const expandedItems = useMemo(() => {
    const out: CalendarItem[] = [];
    for (const it of filteredItems) {
      if (it.source_kind === 'event' && it.recurrence === 'weekly') {
        out.push(...expandOccurrences(it, range.from, range.to));
      } else {
        out.push(it);
      }
    }
    return out;
  }, [filteredItems, range]);

  // ---- Per-person helpers (only meaningful in multiPerson mode) -------------
  const ownerLabel = useCallback(
    (id: string | null | undefined): string => {
      if (!id) return 'Unassigned';
      if (id === user?.id) return 'You';
      return byId.get(id)?.name || 'Teammate';
    },
    [byId, user?.id]
  );
  const leadingDot = (it: CalendarItem) => (multiPerson ? personColor(it.owner_user_id).dot : dotCls(it));

  // Distinct recruiters currently on screen, for the "Calendars:" legend.
  const shownOwners = useMemo(() => {
    if (!multiPerson) return [] as { id: string; label: string }[];
    const seen = new Map<string, string>();
    for (const it of filteredItems) {
      const key = it.owner_user_id || '';
      if (!seen.has(key)) seen.set(key, ownerLabel(it.owner_user_id));
    }
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [multiPerson, filteredItems, ownerLabel]);

  // Group by local day; all-day events sort before timed within a day. A
  // multi-day event (e.g. a 3-day all-day OOO hold, or a timed block crossing
  // midnight) is fanned onto EVERY local day it covers, not just its start —
  // clamped to the visible range so a long span can't spin the loop.
  const itemsByDay = useMemo(() => {
    const map = new Map<string, CalendarItem[]>();
    const push = (k: string, it: CalendarItem) => {
      const arr = map.get(k);
      if (arr) arr.push(it);
      else map.set(k, [it]);
    };
    const rangeStart = startOfDay(range.from);
    const rangeEnd = startOfDay(range.to);
    for (const it of expandedItems) {
      const first = startOfDay(new Date(it.start));
      const last = startOfDay(new Date(it.end));
      let d = first < rangeStart ? rangeStart : first;
      const end = last > rangeEnd ? rangeEnd : last;
      if (d > end) {
        push(dayKey(it.start), it); // wholly outside the window (shouldn't happen) — keep on start day
        continue;
      }
      for (; d <= end; d = addDays(d, 1)) push(localDayKey(d), it);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.all_day === b.all_day ? a.start.localeCompare(b.start) : a.all_day ? -1 : 1));
    }
    return map;
  }, [expandedItems, range]);

  const handleItemClick = (item: CalendarItem) => {
    if (item.source_kind === 'event' && item.editable) {
      // Edit the SERIES (the base row), not the clicked occurrence, so the true
      // start date + recurrence rule are preserved.
      const base = baseEventsById.get(item.source_id) || item;
      setModal({
        mode: 'edit',
        eventId: base.source_id,
        title: base.title,
        start: new Date(base.start),
        end: new Date(base.end),
        allDay: base.all_day,
        location: base.location ?? null,
        description: base.description ?? null,
        color: base.color ?? null,
        recurrence: base.recurrence ?? null,
        recurrenceDays: base.recurrence_days ?? null,
        recurrenceUntil: base.recurrence_until ?? null,
        blocksBooking: base.blocks_booking ?? true,
      });
    } else if (item.contact_id) {
      navigate(`/crm/contacts/${item.contact_id}`);
    }
  };

  const openCreate = (day?: Date) => {
    if (!canCreate) return;
    let base: Date;
    if (day) {
      base = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0, 0, 0);
    } else {
      base = new Date();
      base.setMinutes(0, 0, 0);
      base.setHours(base.getHours() + 1);
    }
    setModal({ mode: 'create', start: base, end: new Date(base.getTime() + 30 * 60000), allDay: false, color: DEFAULT_EVENT_COLOR });
  };

  const monthLabel = anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  // ---- Month grid ----------------------------------------------------------
  const monthCells = useMemo(() => {
    const gridStart = startOfWeekSun(startOfMonth(anchor));
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [anchor]);

  const todayKey = localDayKey(new Date());

  const renderMonth = () => (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="grid grid-cols-7 border-b border-gray-100 dark:border-gray-700">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            <span className="hidden sm:inline">{w}</span>
            <span className="sm:hidden">{w[0]}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {monthCells.map((cell) => {
          const key = localDayKey(cell);
          const dayItems = itemsByDay.get(key) || [];
          const otherMonth = cell.getMonth() !== anchor.getMonth();
          const isToday = key === todayKey;
          const shown = dayItems.slice(0, 3);
          const overflow = dayItems.length - shown.length;
          return (
            <div
              key={key}
              onClick={() => openCreate(cell)}
              className={`min-h-[92px] border-b border-r border-gray-100 p-1.5 dark:border-gray-700 sm:min-h-[96px] 2xl:min-h-[116px] ${
                canCreate ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40' : ''
              } ${otherMonth ? 'bg-gray-50/60 dark:bg-gray-900/30' : ''}`}
            >
              <div className="mb-1 flex items-center justify-between">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                    isToday
                      ? 'bg-primary-600 text-white'
                      : otherMonth
                      ? 'text-gray-400 dark:text-gray-600'
                      : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {cell.getDate()}
                </span>
              </div>
              <div className="space-y-1">
                {shown.map((it) => {
                  const terminal = isTerminal(it);
                  return (
                    <button
                      key={keyOf(it)}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleItemClick(it);
                      }}
                      title={multiPerson ? `${ownerLabel(it.owner_user_id)} · ${it.title}` : it.title}
                      className={`flex w-full items-center truncate text-left ${chipBase} ${chipCls(it)} ${terminal ? 'line-through opacity-60' : ''}`}
                    >
                      {multiPerson && <span className={`mr-1 h-1.5 w-1.5 flex-shrink-0 rounded-full ${personColor(it.owner_user_id).dot}`} />}
                      {!it.all_day && <span className="mr-1 hidden tabular-nums opacity-70 sm:inline">{clockTime(it.start)}</span>}
                      <span className="truncate">{it.title}</span>
                    </button>
                  );
                })}
                {overflow > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDayDetail(cell);
                    }}
                    className="block w-full text-left text-[11px] font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400"
                  >
                    +{overflow} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ---- Agenda --------------------------------------------------------------
  const agendaDays = useMemo(() => {
    const keys = Array.from(itemsByDay.keys()).sort();
    return keys.map((k) => ({ key: k, items: itemsByDay.get(k)! }));
  }, [itemsByDay]);

  const renderAgenda = () => {
    if (agendaDays.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center dark:border-gray-700 dark:bg-gray-800">
          <CalendarDays className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Nothing scheduled in the next 30 days.</p>
          {canCreate && (
            <button onClick={() => openCreate()} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700">
              <Plus className="h-4 w-4" /> New event
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {agendaDays.map(({ key, items: dayItems }) => (
          <div key={key} className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="border-b border-gray-100 px-4 py-2 text-sm font-semibold text-gray-900 dark:border-gray-700 dark:text-gray-100">
              {dayLabel(`${key}T12:00:00`)}
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {dayItems.map((it) => {
                const terminal = isTerminal(it);
                const ec = it.source_kind === 'event' ? eventColor(it.color) : null;
                const border = multiPerson
                  ? personColor(it.owner_user_id).border
                  : ec
                  ? ec.border
                  : it.source_kind === 'callback'
                  ? 'border-cyan-400'
                  : 'border-amber-400';
                return (
                  <button
                    key={keyOf(it)}
                    type="button"
                    onClick={() => handleItemClick(it)}
                    className={`flex w-full items-center gap-3 border-l-2 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 ${border}`}
                  >
                    <span className="w-20 flex-shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                      {it.all_day ? 'All day' : clockTime(it.start)}
                    </span>
                    <span className={`h-2 w-2 flex-shrink-0 rounded-full ${leadingDot(it)}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-sm font-medium text-gray-900 dark:text-gray-100 ${terminal ? 'line-through opacity-60' : ''}`}>
                        {multiPerson && (
                          <span className="font-normal text-gray-500 dark:text-gray-400">{firstName(ownerLabel(it.owner_user_id))} · </span>
                        )}
                        {it.title}
                      </span>
                      {(it.location || it.note) && (
                        <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{it.location || it.note}</span>
                      )}
                    </span>
                    <span className={`flex-shrink-0 ${chipBase} ${chipCls(it)} ${terminal ? 'opacity-60' : ''}`}>
                      {it.source_kind === 'callback' ? 'Callback' : it.source_kind === 'task' ? 'Task' : 'Event'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ---- Day-detail popover (month "+N more") --------------------------------
  const renderDayDetail = () => {
    if (!dayDetail) return null;
    const dayItems = itemsByDay.get(localDayKey(dayDetail)) || [];
    return (
      <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 p-4" onClick={() => setDayDetail(null)}>
        <div className="max-h-[80vh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-4 shadow-xl dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {dayDetail.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
            </h3>
            <button type="button" onClick={() => setDayDetail(null)} aria-label="Close" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-1">
            {dayItems.map((it) => {
              const terminal = isTerminal(it);
              return (
                <button
                  key={keyOf(it)}
                  type="button"
                  onClick={() => {
                    setDayDetail(null);
                    handleItemClick(it);
                  }}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  <span className={`h-2 w-2 flex-shrink-0 rounded-full ${leadingDot(it)}`} />
                  <span className="w-16 flex-shrink-0 text-xs tabular-nums text-gray-500">{it.all_day ? 'All day' : clockTime(it.start)}</span>
                  <span className={`min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-gray-100 ${terminal ? 'line-through opacity-60' : ''}`}>
                    {multiPerson && <span className="text-gray-500 dark:text-gray-400">{firstName(ownerLabel(it.owner_user_id))} · </span>}
                    {it.title}
                  </span>
                </button>
              );
            })}
          </div>
          {canCreate && (
            <button
              onClick={() => {
                const d = dayDetail;
                setDayDetail(null);
                openCreate(d);
              }}
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Plus className="h-4 w-4" /> New event this day
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {view === 'month' ? monthLabel : 'Next 30 days'}
          </h1>
          {view === 'month' && (
            <div className="ml-1 flex items-center gap-1">
              <button type="button" onClick={() => setAnchor((a) => addMonths(a, -1))} aria-label="Previous month" className="rounded-lg p-2.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 sm:p-1.5">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => setAnchor(new Date())} className="rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
                Today
              </button>
              <button type="button" onClick={() => setAnchor((a) => addMonths(a, 1))} aria-label="Next month" className="rounded-lg p-2.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 sm:p-1.5">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {isAdmin && team.length > 0 && (
            <RecruiterMultiSelect team={team} currentUserId={user?.id} value={assignees} onChange={updateAssignees} />
          )}

          <SegmentedControl
            label="Calendar view"
            value={view}
            onChange={(v) => setViewPref(v as 'month' | 'agenda')}
            options={[
              { value: 'month', label: 'Month', icon: <CalendarRange className="h-4 w-4" /> },
              { value: 'agenda', label: 'Agenda', icon: <CalendarDays className="h-4 w-4" /> },
            ]}
          />

          <RefreshButton onClick={() => load()} loading={loading} label="Refresh calendar" />

          <button
            type="button"
            onClick={() => openCreate()}
            disabled={!canCreate}
            title={canCreate ? 'New event' : 'Include your own calendar to add events'}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> New event
          </button>
        </div>
      </div>

      {/* Event-type toggles (the per-type "calendars") + per-person legend */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        {ALL_KINDS.map((k) => {
          const on = visibleKinds.has(k);
          const meta = KIND_META[k];
          return (
            <button
              key={k}
              type="button"
              onClick={() => toggleKind(k)}
              aria-pressed={on}
              title={on ? `Hide ${meta.label.toLowerCase()}` : `Show ${meta.label.toLowerCase()}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium transition ${
                on
                  ? 'border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200'
                  : 'border-dashed border-gray-300 text-gray-400 dark:border-gray-600 dark:text-gray-500'
              }`}
            >
              <span className={`h-2 w-2 rounded-full ${meta.dot} ${on ? '' : 'opacity-40'}`} />
              {meta.label}
            </button>
          );
        })}

        {multiPerson && shownOwners.length > 0 && (
          <div className="ml-1 flex flex-wrap items-center gap-2 border-l border-gray-200 pl-3 dark:border-gray-700">
            <span className="text-gray-400">Calendars:</span>
            {shownOwners.map((o) => (
              <span key={o.id || 'unassigned'} className="inline-flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${personColor(o.id || null).dot}`} />
                {o.label}
              </span>
            ))}
          </div>
        )}

        {includesOthers && !multiPerson && (
          <span className="text-gray-400">· Showing another recruiter's callbacks &amp; tasks (their personal events stay private)</span>
        )}
        {includesOthers && multiPerson && (
          <span className="text-gray-400">· Personal events stay private to each owner</span>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      ) : view === 'month' ? (
        renderMonth()
      ) : (
        renderAgenda()
      )}

      {modal && (
        <EventModal
          eventId={modal.mode === 'edit' ? modal.eventId : null}
          initialTitle={modal.mode === 'edit' ? modal.title : ''}
          initialStart={modal.start}
          initialEnd={modal.end}
          initialAllDay={modal.allDay}
          initialLocation={modal.mode === 'edit' ? modal.location : ''}
          initialDescription={modal.mode === 'edit' ? modal.description : ''}
          initialColor={modal.color || DEFAULT_EVENT_COLOR}
          initialRecurrence={modal.mode === 'edit' ? modal.recurrence : null}
          initialRecurrenceDays={modal.mode === 'edit' ? modal.recurrenceDays : null}
          initialRecurrenceUntil={modal.mode === 'edit' ? modal.recurrenceUntil : null}
          initialBlocksBooking={modal.mode === 'edit' ? modal.blocksBooking : true}
          onClose={() => setModal(null)}
          onSaved={() => load(true)}
        />
      )}

      {renderDayDetail()}
    </div>
  );
}
